import { create } from "zustand";
import { unstable_batchedUpdates } from "react-dom";

interface IssueSelectionState {
  selectedIssueIds: Set<string>;
  lastSelectedIssueId: string | null;
  toggleIssueSelection: (issueId: string) => void;
  selectSingleIssue: (issueId: string) => void;
  addToSelection: (issueId: string) => void;
  removeFromSelection: (issueId: string) => void;
  selectAllVisible: (issueIds: string[]) => void;
  selectAllInColumn: (issueIds: string[]) => void;
  clearSelection: () => void;
  clearSelectionOnly: () => void;
  isSelected: (issueId: string) => boolean;
}

export const useIssueSelectionStore = create<IssueSelectionState>(
  (set, get) => ({
    selectedIssueIds: new Set<string>(),
    lastSelectedIssueId: null,
    isSelected: (issueId: string) => {
      return get().selectedIssueIds.has(issueId);
    },
    toggleIssueSelection: (issueId: string) => {
      const state = get();
      const newSelectedIssueIds = new Set(state.selectedIssueIds);
      let newLastSelectedIssueId = state.lastSelectedIssueId;

      if (newSelectedIssueIds.has(issueId)) {
        newSelectedIssueIds.delete(issueId);
        if (state.lastSelectedIssueId === issueId) {
          // Find a new last selected issue if any remain
          if (newSelectedIssueIds.size > 0) {
            newLastSelectedIssueId =
              Array.from(newSelectedIssueIds).pop() || null;
          } else {
            newLastSelectedIssueId = null;
          }
        }
      } else {
        newSelectedIssueIds.add(issueId);
        newLastSelectedIssueId = issueId;
      }

      set({
        selectedIssueIds: newSelectedIssueIds,
        lastSelectedIssueId: newLastSelectedIssueId,
      });
    },
    selectSingleIssue: (issueId: string) => {
      set({
        selectedIssueIds: new Set([issueId]),
        lastSelectedIssueId: issueId,
      });
    },
    addToSelection: (issueId: string) => {
      const state = get();
      const newSelectedIssueIds = new Set(state.selectedIssueIds);
      newSelectedIssueIds.add(issueId);
      let newLastSelectedIssueId = state.lastSelectedIssueId;
      if (state.lastSelectedIssueId === issueId) {
        newLastSelectedIssueId = issueId;
      } else {
        newLastSelectedIssueId = null;
      }

      set({
        selectedIssueIds: newSelectedIssueIds,
        lastSelectedIssueId: newLastSelectedIssueId,
      });
    },
    removeFromSelection: (issueId: string) => {
      const state = get();
      const newSelectedIssueIds = new Set(state.selectedIssueIds);
      newSelectedIssueIds.delete(issueId);

      let newLastSelectedIssueId = state.lastSelectedIssueId;
      if (state.lastSelectedIssueId === issueId) {
        if (newSelectedIssueIds.size > 0) {
          newLastSelectedIssueId =
            Array.from(newSelectedIssueIds).pop() || null;
        } else {
          newLastSelectedIssueId = null;
        }
      }

      set({
        selectedIssueIds: newSelectedIssueIds,
        lastSelectedIssueId: newLastSelectedIssueId,
      });
    },
    selectAllVisible: (issueIds: string[]) => {
      unstable_batchedUpdates(() => {
        set({
          selectedIssueIds: new Set(issueIds),
        });
      });
    },
    selectAllInColumn: (issueIds: string[]) => {
      unstable_batchedUpdates(() => {
        set({
          selectedIssueIds: new Set(issueIds),
        });
      });
    },
    clearSelection: () => {
      set({
        selectedIssueIds: new Set(),
        lastSelectedIssueId: null,
      });
    },
    clearSelectionOnly: () => {
      set({
        selectedIssueIds: new Set(),
      });
    },
  }),
);
